import {
  Typography,
  Box,
  Card,
  Grid,
  Avatar,
  Autocomplete,
  TextField,
  InputAdornment,
  IconButton,
  Container,
  alpha,
  useTheme,
  Chip,
} from "@mui/material";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getStudents, clearTutor } from "../../state/slices/tutor";
import { useSelector, useDispatch } from "react-redux";
import Loading from "../../components/loading/loading";
import { Search, Person, Phone, Email, Schedule, Quiz, Collections, Add } from "@mui/icons-material";
import dayjs from "dayjs";

const TutorClassRoom = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const theme = useTheme();
  const { students, loading, errorMsg, successMsg } = useSelector(
    (state) => state.tutor
  );
  const [searchStudent, setSearchStudent] = React.useState([]);

  const handleStudentsFetch = () => {
    dispatch(getStudents());
  };

  useEffect(() => {
    handleStudentsFetch();
  }, [dispatch]);

  useEffect(() => {
    if (successMsg) {
      setSearchStudent(students);
      dispatch(clearTutor());
    }
  }, [successMsg, students, dispatch]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: alpha(theme.palette.primary.main, 0.03),
        pb: 4,
      }}
    >
      <Container maxWidth="lg" sx={{ pt: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: theme.palette.primary.main,
            mb: 3,
            textAlign: 'center',
          }}
        >
          {t('classRoom.tutorClassRoom')}
        </Typography>

        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={4}>
            <Card
              onClick={() => navigate("/quiz/create")}
              sx={{
                p: 2,
                cursor: 'pointer',
                textAlign: 'center',
                border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                }
              }}
            >
              <Quiz sx={{ fontSize: 40, color: theme.palette.primary.main, mb: 1 }} />
              <Typography variant="subtitle1" fontWeight={600}>{t('classRoom.createQuiz')}</Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card
              onClick={() => navigate("/flashcard/create")}
              sx={{
                p: 2,
                cursor: 'pointer',
                textAlign: 'center',
                border: `2px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                '&:hover': {
                  borderColor: theme.palette.secondary.main,
                  bgcolor: alpha(theme.palette.secondary.main, 0.05),
                }
              }}
            >
              <Collections sx={{ fontSize: 40, color: theme.palette.secondary.main, mb: 1 }} />
              <Typography variant="subtitle1" fontWeight={600}>{t('classRoom.createFlashcards')}</Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card
              onClick={() => navigate("/quiz/my")}
              sx={{
                p: 2,
                cursor: 'pointer',
                textAlign: 'center',
                border: `2px solid ${alpha(theme.palette.success.main, 0.2)}`,
                '&:hover': {
                  borderColor: theme.palette.success.main,
                  bgcolor: alpha(theme.palette.success.main, 0.05),
                }
              }}
            >
              <Quiz sx={{ fontSize: 40, color: theme.palette.success.main, mb: 1 }} />
              <Typography variant="subtitle1" fontWeight={600}>{t('classRoom.myQuizzes')}</Typography>
            </Card>
          </Grid>
        </Grid>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 4,
          }}
        >
          <Autocomplete
            onChange={(event, newValue) => {
              if (newValue === null) {
                setSearchStudent(students);
              } else {
                setSearchStudent(
                  students.filter(
                    (student) =>
                      student.student.firstName +
                        " " +
                        student.student.lastName ===
                      newValue
                  )
                );
              }
            }}
            disablePortal
            options={students.map(
              (student) =>
                student.student.firstName + " " + student.student.lastName
            )}
            sx={{
              width: { xs: '100%', sm: 500 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                bgcolor: 'white',
                '&:hover': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  }
                },
                '&.Mui-focused': {
                  bgcolor: 'white',
                  boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
                }
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('classRoom.studentName')}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconButton>
                        <Search sx={{ color: theme.palette.primary.main }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <Loading />
          </Box>
        ) : searchStudent.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '400px',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                mb: 1,
              }}
            >
              {t('classRoom.noStudentFound')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('classRoom.noStudentsAvailable')}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {searchStudent.map((student) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={student.id}>
                <Card
                  onClick={() => {
                    navigate("/detail-class-room", { state: { student } });
                  }}
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.2)}`,
                      borderColor: theme.palette.primary.main,
                    }
                  }}
                >
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Avatar
                      alt={student.student.firstName + " " + student.student.lastName}
                      src={student.student.profileImage}
                      sx={{
                        width: 100,
                        height: 100,
                        mx: 'auto',
                        mb: 2,
                        border: `3px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.primary.main,
                        mb: 2,
                      }}
                    >
                      {student.student.firstName + " " + student.student.lastName}
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, textAlign: 'left' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Phone sx={{ color: 'text.secondary', fontSize: 18 }} />
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {student.student.phoneNumber}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Email sx={{ color: 'text.secondary', fontSize: 18 }} />
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {student.student.email}
                        </Typography>
                      </Box>
                      {student.upcomingSession && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                          <Schedule sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
                          <Box>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                              {t('classRoom.upcomingClass')}
                            </Typography>
                            <Typography variant="body2" sx={{ color: theme.palette.primary.main, fontWeight: 500 }}>
                              {dayjs(student.upcomingSession.startingTime).format("DD/MM/YYYY hh:mm a")} - {dayjs(student.upcomingSession.endingTime).format("hh:mm a")}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      {!student.upcomingSession && (
                        <Chip
                          label={t('classRoom.noUpcomingClass')}
                          size="small"
                          color="default"
                          sx={{ mt: 1 }}
                        />
                      )}
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default TutorClassRoom;
